import {
  Box,
  Grid,
  Typography,
  IconButton,
  Paper,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  DialogContent,
  DialogTitle,
  Dialog,
  DialogActions,
  Button,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useEffect, useRef, useState } from "react";
import { useTenPercentClaimFormStore } from "../../../../../../store/useTenPercentClaimFormStore";
import { getDocument } from "../../../../../../services/dashboard";

type TenPercentDocumentKeys =
  | "identity_document"
  | "selfie_with_identity"
  | "liveness_check"
  | "home_affairs_verification"
  | "bank_proof";

type DocumentItem = {
  key: TenPercentDocumentKeys;
  label: string;
  name: string;
  size?: string;
  file?: File;
  link?: string;
  deleted?: boolean;
  documentId: string;
};

export default function DocumentVerificationForm({
  documentData,
  iceClaimNumber,
  isAssigned,
  claimStatus,
  documentComment,
  onDocumentInfoCommentChange,
  commentData,
  isEditable,
  onDocumentDeleteChange,
  onDocumentFileChange,
}: any) {
  const [localDocs, setLocalDocs] = useState(documentData);
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);
  const fileInputs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const [fileErrors, setFileErrors] = useState<Record<string, string>>({});

  const { addTenPercentDocument } = useTenPercentClaimFormStore();
  const { tenPercentdocuments } = useTenPercentClaimFormStore();

  const handleClosePreview = () => {
    setOpenPreview(false);
    setSelectedDoc(null);
  };

  const handleDeleteDoc = (index: number) => {
    const updatedDocs = [...localDocs];
    updatedDocs[index].deleted = true;
    setLocalDocs(updatedDocs);
    if (onDocumentDeleteChange) {
      onDocumentDeleteChange(updatedDocs[index].key);
    }
  };

  useEffect(() => {
    if (!documentData || !tenPercentdocuments) return;

    const updatedDocs = (documentData as DocumentItem[]).map((doc) => {
      const existing = localDocs?.find((d: any) => d.key === doc.key);
      const updatedFile = tenPercentdocuments[doc.key];

      if (updatedFile instanceof File) {
        return {
          ...doc,
          name: updatedFile.name,
          size: `${(updatedFile.size / 1024).toFixed(1)} KB`,
          file: updatedFile,
          deleted: existing?.deleted ?? false,
          document_id: doc.documentId,
        };
      }
      return {
        ...doc,
        deleted: existing?.deleted ?? false,
        document_id: doc.documentId,
      };
    });

    setLocalDocs(updatedDocs);
  }, [tenPercentdocuments, documentData]);

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const updatedDocs = [...localDocs];
    const docKey = updatedDocs[index].key;

    updatedDocs[index] = {
      ...updatedDocs[index],
      name: file.name,
      size: `${(file.size / 1024).toFixed(1)} KB`,
      file,
      deleted: false,
    };

    setLocalDocs(updatedDocs);
    if (docKey) {
      addTenPercentDocument(docKey, file);
    }
    if (onDocumentFileChange) {
      onDocumentFileChange(docKey, file);
    }
  };

  const comments = commentData?.filter(
    (comment: any) => comment.section_type === "DOCUMENT_VERIFICATION"
  );

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    return `${String(d.getDate()).padStart(2, "0")}-${String(
      d.getMonth() + 1
    ).padStart(2, "0")}-${d.getFullYear()}`;
  };

  const handlePreview = async (doc: any) => {
    try {
      const res = await getDocument(iceClaimNumber, doc.document_id);
      if (!res || res.status !== 200) {
        throw new Error("Failed to fetch document");
      }

      const blob = res.data;
      const url = URL.createObjectURL(blob);

      setSelectedDoc({ ...doc, file_url: url });
      setOpenPreview(true);
    } catch (err) {
      console.error("Error fetching file:", err);
    }
  };

  const handleDownload = async (doc: any) => {
    try {
      const res = await getDocument(iceClaimNumber, doc.document_id);
      if (!res || res.status !== 200) {
        throw new Error("Failed to download document");
      }

      const blob = res.data;
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = doc.name || "document.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading file:", err);
    }
  };

  return (
    <Box p={4}>
      <Grid
        container
        spacing={0}
        rowSpacing={6}
        columnSpacing={12}
        justifyContent="center"
        width="80%"
      >
        {localDocs.map((doc: any, index: number) => (
          <Grid size={{ md: 6, xs: 12 }} key={index}>
            <Typography
              fontWeight={600}
              mb={0.5}
              fontSize={18}
              textAlign="left"
            >
              {doc.label}
            </Typography>

            {!doc.deleted ? (
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: "#F5EEE9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  py: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    background: "#F5EEE9",
                    color: "#FF0000",
                    borderRadius: 1,
                    py: 0.5,
                  }}
                >
                  <PictureAsPdfIcon />
                  <Box>
                    <Typography fontSize={14} fontWeight={500}>
                      {doc.name}
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  {isEditable ? (
                    <IconButton
                      onClick={() => handleDeleteDoc(index)}
                      size="small"
                    >
                      <DeleteIcon sx={{ color: "red" }} />
                    </IconButton>
                  ) : (
                    <IconButton onClick={() => handlePreview(doc)} size="small">
                      <VisibilityIcon sx={{ color: "#EC6E00" }} />
                    </IconButton>
                  )}
                </Box>
              </Paper>
            ) : (
              <>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => fileInputs.current[doc.key]?.click()}
                  sx={{ mt: 1 }}
                >
                  Choose File
                </Button>
                <input
                  type="file"
                  accept="application/pdf"
                  style={{ display: "none" }}
                  ref={(el: any) => (fileInputs.current[doc.key] = el)}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size <= 2 * 1024 * 1024) {
                        handleFileChange(e, index);
                        setFileErrors((prev) => ({ ...prev, [doc.key]: "" }));
                      } else {
                        setFileErrors((prev) => ({
                          ...prev,
                          [doc.key]: "File must be less than 2MB",
                        }));
                      }
                    }
                  }}
                />
                {fileErrors[doc.key] && (
                  <Typography color="error" fontSize={14}>
                    {fileErrors[doc.key]}
                  </Typography>
                )}
              </>
            )}
          </Grid>
        ))}
      </Grid>

      {comments?.length > 0 && (
        <Grid size={{ md: 6, xs: 12 }}>
          <Typography fontWeight={600} fontSize={18} my={2} textAlign="left">
            Comments
          </Typography>
          {comments.map((comment: any, index: number) => (
            <Accordion
              key={index}
              sx={{
                backgroundColor: "#F7F7F7",
                boxShadow: "none",
                borderRadius: "6px",
                mb: 2,
                border: "1px solid #E0E0E0",
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box>
                  <Typography fontWeight={600} fontSize={14}>
                    {comment.commentedByName}{" "}
                    <Typography
                      component="span"
                      fontWeight={600}
                      color="#EC6E00"
                    >
                      - {comment.role_at_time_of_comment?.toUpperCase()}
                    </Typography>
                  </Typography>
                  <Typography fontSize={12} color="text.secondary">
                    {new Date(comment.created_on).toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "2-digit",
                    })}{" "}
                    | {formatDate(comment.created_on)}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography fontSize={15} color="#333" textAlign="left">
                  {comment.content}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Grid>
      )}

      {isAssigned && claimStatus !== "PAID" && (
        <Grid size={12}>
          <Typography
            variant="body1"
            fontWeight={600}
            fontSize={18}
            textAlign="left"
          >
            Comment
          </Typography>
          <TextField
            multiline
            rows={4}
            variant="outlined"
            fullWidth
            disabled={isEditable}
            value={documentComment}
            onChange={onDocumentInfoCommentChange}
          />
        </Grid>
      )}

      {/* Preview Dialog */}
      <Dialog
        open={openPreview}
        onClose={handleClosePreview}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Document Preview</DialogTitle>
        <DialogContent dividers>
          {selectedDoc ? (
            <object
              data={selectedDoc.file_url}
              type="application/pdf"
              width="100%"
              height="600px"
            >
              <p>
                PDF preview not supported.{" "}
                <a
                  href={selectedDoc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download PDF
                </a>
              </p>
            </object>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleDownload(selectedDoc)}
            color="primary"
            variant="outlined"
          >
            Download
          </Button>
          <Button
            onClick={handleClosePreview}
            color="secondary"
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
